import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Button,
  Skeleton,
  CircularProgress,
  Chip,
  Divider,
  Dialog,
  IconButton,
  DialogContent,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  app_title,
  currencySymbol,
  locale,
  ptOffersPageID,
  ptGLCatID,
  ptDomainTracking,
  s3StaticImagePath,
} from "../config";
import { isPrimaryDomain, pixelTrackingEvent, removeCookieAsync } from "../utils";
import { useDispatch, useSelector } from "react-redux";
import { updateGlEditUserDetail } from "../utils/reduxSlice/tempSlice";
import axios from "axios";
import { endpoint } from "../config";
import { customToast } from "../lib/toastLib";
import NotificationDialog from "../components/NotificationDialog";
import { setProductId, setProductType } from "../utils/reduxSlice/tokenSlice";
import applicationLendersInfoApi from "../hooks/POST/applicationLendersInfo";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CcOffersApi from "../hooks/GET/ccOffer";
import CcOfferStatusApi from "../hooks/GET/ccOfferStatusApi";

// Hoisted out of the component so it isn't rebuilt on every render.
const CASHE_GL_LOGO = `${s3StaticImagePath}/1762923605-EYCErajaO1.png`;
const CASHE_GL_NAME = "CasheGL";
const OFFERS_POLL_INTERVAL_MS = 5000;

const formatCurrency = (amount) =>
  `${currencySymbol} ${Intl.NumberFormat(locale).format(amount || 0)}`;

/** Card used for the "Available Offers" grid (matched, applyable offers). */
const MatchedOfferCard = React.memo(function MatchedOfferCard({
  option,
  idx,
  loading,
  applyOfferLoading,
  onApply,
  onToggleExpand,
  t,
}) {
  if (option?.name === CASHE_GL_NAME) {
    return (
      <Grid item xs={12} sm={8} md={4}>
        <Card sx={{ padding: 1, height: { md: "100%" }, display: "flex", flexDirection: "column" }} variant="outlined">
          {loading ? (
            <Skeleton variant="rectangular" width="100%" height={100} animation="wave" />
          ) : (
            <Grid container>
              <Grid item xs={12} sx={{ cursor: "pointer" }} onClick={(e) => onApply(e, option, idx)}>
                <img src={CASHE_GL_LOGO} alt="lender_logo" style={{ height: "100%", width: "100%" }} />
              </Grid>
            </Grid>
          )}
        </Card>
      </Grid>
    );
  }

  return (
    <Grid item xs={12} sm={8} md={4}>
      <Card sx={{ padding: 1, height: { md: "100%" }, display: "flex", flexDirection: "column" }} variant="outlined">
        {loading ? (
          <Skeleton variant="rectangular" width="100%" height={100} animation="wave" />
        ) : (
          <Grid container columnSpacing={2} sx={{ alignItems: "center", justifyContent: "space-between" }} minHeight={80}>
            <Grid item xs={6}>
              <CardMedia
                component="img"
                style={{ objectFit: "contain" }}
                image={option.logo}
                alt={option.name}
                onClick={(e) => onApply(e, option, idx)}
                sx={{ cursor: "pointer", height: 80 }}
              />
            </Grid>
            <Grid item xs={6} sx={{ textAlign: "end" }}>
              <Typography variant="body2" color="text.secondary">
                {t("Loan amount up to")}
              </Typography>
              <Typography variant="h6" color="secondary" sx={{ fontWeight: "bold" }}>
                {formatCurrency(option.loanAmount)}
              </Typography>
              {option.specialOffer && (
                <Box
                  sx={{
                    width: "fit-content",
                    justifySelf: "end",
                    textAlign: "left",
                    p: 1,
                    mt: 1,
                    bgcolor: "green",
                    borderRadius: "10px",
                    color: "white",
                  }}
                >
                  <Typography sx={{ fontSize: 12, fontWeight: 600 }}>{option.specialOffer}</Typography>
                </Box>
              )}
              {option.preapproved === 1 && <Chip label="Pre Approved" color="success" sx={{ mt: 1.25 }} />}
            </Grid>
          </Grid>
        )}

        <CardActions sx={{ marginTop: 2, display: "flex", justifyContent: "center" }}>
          {loading ? (
            <Skeleton variant="rectangular" width="100%" animation="wave">
              <LoadingButton fullWidth size="large" />
            </Skeleton>
          ) : (
            <Box sx={{ display: "flex", gap: 2, width: "100%" }}>
              <Button
                color="inherit"
                size="medium"
                sx={{ flexGrow: 1 }}
                endIcon={option.expand ? <ExpandLess /> : <ExpandMore />}
                onClick={() => onToggleExpand(idx)}
              >
                {t("Details")}
              </Button>
              <LoadingButton
                size="medium"
                fullWidth
                variant="contained"
                onClick={(e) => onApply(e, option, idx)}
                loading={applyOfferLoading === idx}
                disabled={applyOfferLoading !== -1}
              >
                {t("Apply")}
              </LoadingButton>
            </Box>
          )}
        </CardActions>

        {option?.expand && (
          <CardContent sx={{ textAlign: "center", flexGrow: 1, display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
            {loading ? (
              <Skeleton animation="wave" />
            ) : (
              <>
                <Button color="inherit" size="small" endIcon={option.expand ? <ExpandLess /> : <ExpandMore />} onClick={() => onToggleExpand(idx)}>
                  {t("Details")}
                </Button>
                {option.name === "IndusIndBankBL" && (
                  <Typography variant="body2" color="text.secondary">
                    For loan amount of 5 lacs and above GST is mandatory*
                  </Typography>
                )}
                <Typography variant="body2" color="text.secondary">
                  {`${t("Tenure")}: ${option.tenure} months`}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {`${t("Interest Rate")}: ${option.interestRateText}`}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {`${t("Processing Fees")}: ${option.processingFees}% of loan amount`}
                </Typography>
              </>
            )}
          </CardContent>
        )}
      </Card>
    </Grid>
  );
});

/** Card used for the "Unmatched Lenders" grid — always disabled/informational. */
const UnmatchedOfferCard = React.memo(function UnmatchedOfferCard({ option, idx, loading, t }) {
  if (option?.name === CASHE_GL_NAME) {
    return (
      <Grid item xs={12} sm={8} md={4}>
        <Card sx={{ padding: 1, height: { md: "100%" }, display: "flex", flexDirection: "column" }} variant="outlined">
          {loading ? (
            <Skeleton variant="rectangular" width="100%" height={100} animation="wave" />
          ) : (
            <Grid container>
              <Grid item xs={12}>
                <img src={CASHE_GL_LOGO} alt="lender_logo" style={{ height: "100%", width: "100%" }} />
              </Grid>
            </Grid>
          )}
        </Card>
      </Grid>
    );
  }

  const logo = option?.[0]?.logo;
  const offerAmount = option?.[0]?.offer_amount;

  return (
    <Grid item xs={12} sm={8} md={4}>
      <Card sx={{ padding: 1, height: { md: "100%" }, display: "flex", flexDirection: "column" }} variant="outlined">
        {loading ? (
          <Skeleton variant="rectangular" width="100%" height={100} animation="wave" />
        ) : (
          <Grid container columnSpacing={2} sx={{ alignItems: "center", justifyContent: "space-between" }} minHeight={80}>
            <Grid item xs={6}>
              <CardMedia component="img" style={{ objectFit: "contain" }} image={logo} alt={option?.lender_name} sx={{ height: 80 }} />
            </Grid>
            <Grid item xs={6} sx={{ textAlign: "end" }}>
              <Typography variant="body2" color="text.secondary">
                {t("Loan amount up to")}
              </Typography>
              <Typography variant="h6" color="secondary" sx={{ fontWeight: "bold" }}>
                {formatCurrency(offerAmount)}
              </Typography>
              {option.specialOffer && (
                <Box sx={{ width: "fit-content", justifySelf: "end", textAlign: "left", p: 1, mt: 1, bgcolor: "green", borderRadius: "10px", color: "white" }}>
                  <Typography sx={{ fontSize: 12, fontWeight: 600 }}>{option.specialOffer}</Typography>
                </Box>
              )}
              {option.preapproved === 1 && <Chip label="Pre Approved" color="success" sx={{ mt: 1.25 }} />}
            </Grid>
          </Grid>
        )}

        <CardActions sx={{ marginTop: 2, display: "flex", justifyContent: "center" }}>
          {loading ? (
            <Skeleton variant="rectangular" width="100%" animation="wave">
              <LoadingButton fullWidth size="large" />
            </Skeleton>
          ) : (
            <Box sx={{ display: "flex", gap: 2, width: "100%" }}>
              <Button color="inherit" size="medium" sx={{ flexGrow: 1 }} endIcon={<ExpandMore />} disabled>
                {t("Details")}
              </Button>
              <LoadingButton size="medium" fullWidth variant="contained" disabled>
                {t("Apply")}
              </LoadingButton>
            </Box>
          )}
        </CardActions>
      </Card>
    </Grid>
  );
});

/** One row of the loan-application status table. */
const StatusRow = React.memo(function StatusRow({ row, loading, onApply }) {
  const trimmedStatus = row?.status?.trim?.() ?? "";
  const isDeclined = trimmedStatus === "Declined";
  const isPending = trimmedStatus === "Application Started" || trimmedStatus === "Processing Application";

  return (
    <Grid container mb={2} sx={{ border: "1px solid lightgray", borderRadius: 1, bgcolor: "#FAFAFA" }}>
      <Grid item md={3} sm={6} xs={6} p={1} align="center">
        {loading ? (
          <Skeleton animation="wave" />
        ) : (
          <Box sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
            <Box sx={{ height: 10, width: 10, borderRadius: "50%", bgcolor: isDeclined ? "gray" : "orange" }} />
            <span>{row.lender}</span>
          </Box>
        )}
      </Grid>
      <Grid item md={3} sm={6} xs={6} p={1} align="center">
        {loading ? (
          <Skeleton animation="wave" />
        ) : (
          <Box sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
            {isPending && <Chip sx={{ bgcolor: "white" }} label={row.status} variant="outlined" color="warning" />}
            {isDeclined && <Chip label={row.status} variant="outlined" color="error" />}
            {!isPending && !isDeclined && <Chip label={trimmedStatus} variant="outlined" color="success" />}
          </Box>
        )}
      </Grid>
      <Grid item md={3} sm={6} xs={6} p={1} align="center">
        {loading ? (
          <Skeleton animation="wave" />
        ) : (
          <Box sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Typography fontWeight="bold">{formatCurrency(row.amount)}</Typography>
          </Box>
        )}
      </Grid>
      <Grid item md={3} sm={6} xs={6} p={1} align="center">
        {loading ? (
          <Skeleton animation="wave" width="100%">
            <Button />
          </Skeleton>
        ) : (
          <Button
            color="info"
            variant="contained"
            onClick={() => onApply(row.application_id, row.lender)}
            disabled={!row.navigationLink || row.status === "Declined"}
          >
            Apply
          </Button>
        )}
      </Grid>
    </Grid>
  );
});

function CreditCardOffers() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const appId = useSelector((state) => state.token.appId);
  const userPhone = useSelector((state) => state.user.phoneNumber) || "";

  const [applyOfferLoading, setApplyOfferLoading] = useState(-1);

  const [statusList, setStatusList] = useState([]);
  const [unMatchedStatusList, setUnMatchedStatusList] = useState([]);
  const [statusListLoading, setStatusListLoading] = useState(true);

  const [lendersConfig, setLendersConfig] = useState("");
  const [lenderMessage, setLenderMessage] = useState({ message: "", logo: "" });
  const [openOfferDialog, setOpenOfferDialog] = useState(false);

  const [offersList, setOffersList] = useState([]);
  const [offersListLoading, setOffersListLoading] = useState(true);

  const [notificationDialogState, setNotificationDialogState] = useState({
    open: false,
    header: "",
    messages: [],
  });

  // ---- Loan application status (refetched after every offer dialog interaction) ----
  useEffect(() => {
    if (!appId) return;
    let cancelled = false;

    CcOfferStatusApi(appId)
      .then((data) => {
        if (cancelled) return;
        setStatusList(data.data);
        setUnMatchedStatusList(data.unmatched);
      })
      .catch((error) => console.log(error))
      .finally(() => !cancelled && setStatusListLoading(false));

    return () => {
      cancelled = true;
    };
  }, [appId, openOfferDialog]);

  // ---- Pixel tracking (fires once per phone number) ----
  useEffect(() => {
    if (!userPhone) return;
    pixelTrackingEvent({
      domain: ptDomainTracking,
      txnID: `${userPhone}.${ptGLCatID}.${new Date().toLocaleDateString("en-CA")}`,
      conditions: {
        category_id: String(ptGLCatID),
        goal_id: String(ptOffersPageID),
      },
    });
  }, [userPhone]);

  // ---- Offers list, polled every 5s; preserves each card's expand/collapse state across polls ----
  useEffect(() => {
    if (!appId) return;
    let cancelled = false;

    const fetchCcOffers = async () => {
      try {
        const data = await CcOffersApi(appId);
        if (cancelled) return;
        setOffersList((prevOffersList) =>
          data.map((obj) => {
            const existingOffer = prevOffersList.find(
              (offer) => offer.name === obj.name && offer.loanAmount === obj.loanAmount
            );
            return { ...obj, expand: existingOffer ? existingOffer.expand : false };
          })
        );
      } catch (error) {
        console.log(error);
      } finally {
        if (!cancelled) setOffersListLoading(false);
      }
    };

    fetchCcOffers();
    const intervalId = setInterval(fetchCcOffers, OFFERS_POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [appId]);

  const handleNotificationDialogClose = useCallback(() => {
    setNotificationDialogState((prev) => ({ ...prev, open: false }));
  }, []);

  const handleExpandDetail = useCallback((idx) => {
    setOffersList((prev) => prev.map((item, i) => (i === idx ? { ...item, expand: !item.expand } : item)));
  }, []);

  const getNavigationLink = useCallback((navigationLink, applicationId) => {
    return navigationLink.includes("?")
      ? `${navigationLink}&applicationId=${applicationId}`
      : `${navigationLink}?applicationId=${applicationId}`;
  }, []);

  // Shared tail: given an applicationId + lender name, fetch lender routing info,
  // open the lender's URL, and drive the offer dialog / loading state.
  // Both "apply on a fresh offer" and "resume an existing application" end here,
  // so the logic (and its error handling) lives in exactly one place.
  const openLenderOffer = useCallback(
    (applicationId, lenderName, idx) => {
      return applicationLendersInfoApi(appId, applicationId)
        .then((res) => {
          const lenderInfo = res?.[lenderName?.toLowerCase?.() ?? ""];
          // Guard against an unrecognized lender key instead of throwing on `.defaultUrl`.
          setLenderMessage(lenderInfo || { message: "", logo: "" });
          const url = lenderInfo?.defaultUrl || "";
          setLendersConfig(url);
          if (url) {
            window.open(url, "_blank");
            setOpenOfferDialog(false);
          }
        })
        .catch((error) => {
          setOpenOfferDialog(false);
          customToast.error(error.response?.data?.message || error.message);
        })
        .finally(() => setApplyOfferLoading(-1));
    },
    [appId]
  );

  const handleApplyOffer = useCallback(
    (e, val, idx) => {
      e.preventDefault();
      setOpenOfferDialog(true);
      setApplyOfferLoading(idx);

      const requestBody = {
        creditapp_id: appId,
        amount: val.loanAmount,
        annual_interest_rate: val.interestRate,
        term_months: val.tenure,
        processing_fees: val.processingFees,
        lender_name: val.name,
      };

      axios
        .post(`${endpoint}/api/initiate-loan`, requestBody)
        .then((res) => res.data)
        .then((data) => openLenderOffer(data.applicationId, val.name, idx))
        .catch((error) => {
          setApplyOfferLoading(-1);
          setOpenOfferDialog(false);
          customToast.error(error.response?.data?.message || error.message);
        });
    },
    [appId, openLenderOffer]
  );

  const handleOffer = useCallback(
    (applicationId, lenderName) => {
      setOpenOfferDialog(true);
      setApplyOfferLoading(1);
      openLenderOffer(applicationId, lenderName, 1);
    },
    [openLenderOffer]
  );

  const handleReturnToProfile = useCallback(
    (e) => {
      e.preventDefault();
      dispatch(updateGlEditUserDetail(true));
      navigate("/credit-cards");
    },
    [dispatch, navigate]
  );

  const handleReturnToProducts = useCallback(async () => {
    await removeCookieAsync("productType");
    dispatch(setProductType(null));
    await removeCookieAsync("productId");
    dispatch(setProductId(null));
    navigate(isPrimaryDomain ? "/" : "/products");
  }, [dispatch, navigate]);

  const offersCountLabel = useMemo(() => {
    const count = offersList.length;
    return { count, unit: count === 1 ? t("offer") : t("offers") };
  }, [offersList.length, t]);

  const showOffersHeader = offersListLoading || offersList.length > 0;
  const matchedOfferSkeletons = offersListLoading ? Array.from(new Array(2)) : offersList;
  const unmatchedOfferSkeletons = offersListLoading ? Array.from(new Array(2)) : unMatchedStatusList;
  const showUnableToProceed = !lendersConfig && applyOfferLoading === -1;

  return (
    <>
      <Box sx={{ overflow: "auto", paddingY: 5, minHeight: "82vh" }}>
        <Helmet>
          <title>Offers {app_title && `| ${app_title}`}</title>
          {process.env.REACT_APP_ENV === "PROD" && (
            <>
              <script async src="https://www.googletagmanager.com/gtag/js?id=AW-16753109126"></script>
              <script>{`
                window.dataLayer = window.dataLayer || [];
                function gtag() {
                  dataLayer.push(arguments);
                }
                gtag("js", new Date());
                gtag("config", "AW-16753109126");
              `}</script>
              <script>{`gtag('event', 'conversion', {'send_to': 'AW-16753109126/agKNCMXbvuEZEIbRwLQ-'});`}</script>
            </>
          )}
        </Helmet>

        <Grid container sx={{ minWidth: 300, justifyContent: { xs: "initial", md: "center" } }}>
          <Grid container>
            {showOffersHeader && (
              <Grid mb={5} item xs={12} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Box>
                  <Typography variant="h6" color="secondary" fontWeight="bold">
                    {t("Available Offers")}
                  </Typography>
                  <Typography variant="body2" color="text.primary" sx={{ mt: { xs: 0.5, sm: 0 } }}>
                    <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
                      ({t("You have")} {offersCountLabel.count} {offersCountLabel.unit} {t("available")}.)
                    </Box>
                    <Box component="span" sx={{ display: { xs: "inline", sm: "none" } }}>
                      ({offersCountLabel.count} {offersCountLabel.unit})
                    </Box>
                  </Typography>
                </Box>
                <Button variant="outlined" color="secondary" size="small">
                  <a href="#applications" style={{ textDecoration: "none", color: "inherit", fontWeight: "bold" }}>
                    {t("Your Applications")}
                  </a>
                </Button>
              </Grid>
            )}

            <Grid container spacing={3} sx={{ justifyContent: "center" }}>
              {matchedOfferSkeletons.map((option, idx) => (
                <MatchedOfferCard
                  key={option?.name ? `${option.name}-${option.loanAmount}` : idx}
                  option={option}
                  idx={idx}
                  loading={offersListLoading}
                  applyOfferLoading={applyOfferLoading}
                  onApply={handleApplyOffer}
                  onToggleExpand={handleExpandDetail}
                  t={t}
                />
              ))}
            </Grid>

            {!offersListLoading && offersList.length === 0 && (
              <Grid item xs={12}>
                <Typography variant="h6" color="secondary" align="center" fontWeight="bold" gutterBottom>
                  {t("There are no offers available for you at this moment.")}
                </Typography>
              </Grid>
            )}
          </Grid>

          <Divider sx={{ width: "100%", marginY: 5 }} id="applications" />

          {statusListLoading && (
            <Grid>
              <Grid item xs={12}>
                <Typography variant="h6" color="secondary" align="center" fontWeight="bold" gutterBottom>
                  {t("Status")}
                </Typography>
              </Grid>
              <Grid height={150} sx={{ display: "flex", alignItems: "center" }}>
                <CircularProgress />
              </Grid>
            </Grid>
          )}

          {!statusListLoading && statusList.length !== 0 && (
            <Grid container sx={{ marginBottom: 5 }}>
              <Grid item xs={12}>
                <Typography variant="h6" color="secondary" align="center" fontWeight="bold" gutterBottom mb={5}>
                  {t("Loan Applications")}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Grid container mb={2} sx={{ border: "1px solid lightgray", borderRadius: 1 }}>
                  <Grid item md={3} sm={6} xs={6} p={1} align="center">
                    <Typography fontWeight="bold">{t("Lender")}</Typography>
                  </Grid>
                  <Grid item md={3} sm={6} xs={6} p={1} align="center">
                    <Typography fontWeight="bold">{t("Status")}</Typography>
                  </Grid>
                  <Grid item md={3} sm={6} xs={6} p={1} align="center">
                    <Typography fontWeight="bold">{t("Amount (in ₹)")}</Typography>
                  </Grid>
                  <Grid item md={3} sm={6} xs={6} p={1} align="center">
                    <Typography fontWeight="bold">{t("Apply")}</Typography>
                  </Grid>
                </Grid>
                {(statusListLoading ? Array.from(new Array(3)) : statusList).map((row, idx) => (
                  <StatusRow key={row?.application_id ?? idx} row={row} loading={statusListLoading} onApply={handleOffer} />
                ))}
              </Grid>
            </Grid>
          )}

          {unMatchedStatusList.length > 0 && (
            <Grid item xs={12}>
              <Typography variant="h6" color="secondary" fontWeight="bold" gutterBottom mb={5}>
                {t("Unmatched Lenders")}
              </Typography>
            </Grid>
          )}

          {unMatchedStatusList.length > 0 && (
            <Grid container spacing={3} sx={{ justifyContent: "center" }}>
              {unmatchedOfferSkeletons.map((option, idx) => (
                <UnmatchedOfferCard key={idx} option={option} idx={idx} loading={offersListLoading} t={t} />
              ))}
            </Grid>
          )}

          <Grid container direction="column" alignItems="center" sx={{ marginTop: 5 }} rowSpacing={1}>
            <Grid item>
              <Button size="small" color="secondary" startIcon={<ArrowBackIosIcon />} sx={{ fontWeight: "bold" }} onClick={handleReturnToProfile}>
                {t("Return to Profile")}
              </Button>
            </Grid>
            {!process.env.REACT_APP_SUBDOMAIN && (
              <Grid item>
                <Button size="small" color="secondary" startIcon={<ArrowBackIosIcon />} sx={{ fontWeight: "bold" }} onClick={handleReturnToProducts}>
                  {t("Return to Products")}
                </Button>
              </Grid>
            )}
          </Grid>

          <NotificationDialog
            open={notificationDialogState.open}
            handleClose={handleNotificationDialogClose}
            header={notificationDialogState.header}
            messages={notificationDialogState.messages}
          />
        </Grid>
      </Box>

      <Dialog
        open={openOfferDialog}
        onClose={() => {
          if (applyOfferLoading === -1) setOpenOfferDialog(false);
        }}
        PaperProps={{ sx: { borderRadius: 1, width: "360px", position: "relative", textAlign: "center" } }}
      >
        {applyOfferLoading === -1 && (
          <IconButton
            aria-label="close"
            onClick={() => {
              setOpenOfferDialog(false);
              setApplyOfferLoading(-1);
            }}
            sx={{ position: "absolute", right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}
          >
            <CloseIcon />
          </IconButton>
        )}

        <DialogContent sx={{ display: "flex", flexDirection: "column", alignItems: "center", p: 2 }}>
          {applyOfferLoading !== -1 && (
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
              <CircularProgress size={30} />
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Processing Your Request
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                Your request is being processed through the lender's secure gateway…
              </Typography>
            </Box>
          )}

          {showUnableToProceed && (
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3, mt: 1 }}>
              {lenderMessage?.message?.length > 0 ? (
                <img src={lenderMessage.logo} alt="lender message" style={{ height: 70 }} />
              ) : (
                <>
                  <WarningAmberIcon sx={{ fontSize: 48, color: "warning.main" }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Unable to Proceed
                  </Typography>
                </>
              )}
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                {lenderMessage?.message?.length > 0
                  ? lenderMessage.message
                  : "Unfortunately, your application cannot be processed at this time; however, we have alternative loan offers that may be a fit for you."}
              </Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

export default CreditCardOffers;